import React, { useState, useEffect, useMemo } from 'react';
import { useBalanceVisibility } from '../contexts/BalanceVisibilityContext.tsx';
import { api } from '../services/api.ts';
import { formatDate, normalizeDate, formatCurrency } from '../utils/dateUtils.ts';
import { maskCurrency } from '../utils/balanceMask.ts';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import './Reports.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

interface Account {
  id: number;
  name: string;
  balance: number;
  currency?: string;
  is_investment?: boolean;
  server_id?: number;
}

interface Category {
  id: number;
  name: string;
  type: 'income' | 'expense';
  icon?: string;
  icon_color?: string;
  server_id?: number;
}

interface Transaction {
  id: number;
  description: string;
  amount: number;
  type: 'income' | 'expense' | 'transfer';
  due_date: string;
  paid_date?: string;
  is_paid: boolean;
  account_id: number;
  category_id: number;
  account: { name: string };
  category: { name: string; icon?: string; icon_color?: string };
}

const CHART_COLORS = [
  '#667eea', '#764ba2', '#f093fb', '#4facfe', '#00f2fe',
  '#43e97b', '#fa709a', '#fee140', '#a1c4fd', '#ffd1ff',
  '#f59e0b', '#10b981', '#ef4444', '#3b82f6', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#84cc16', '#06b6d4',
];

const MONTHS = [
  { value: 'all', label: 'Todos' },
  { value: '01', label: 'Janeiro' },
  { value: '02', label: 'Fevereiro' },
  { value: '03', label: 'Março' },
  { value: '04', label: 'Abril' },
  { value: '05', label: 'Maio' },
  { value: '06', label: 'Junho' },
  { value: '07', label: 'Julho' },
  { value: '08', label: 'Agosto' },
  { value: '09', label: 'Setembro' },
  { value: '10', label: 'Outubro' },
  { value: '11', label: 'Novembro' },
  { value: '12', label: 'Dezembro' },
];

const Reports: React.FC = () => {
  const { isVisible } = useBalanceVisibility();

  const now = new Date();
  const [filterMonth, setFilterMonth] = useState(String(now.getMonth() + 1).padStart(2, '0'));
  const [filterYear, setFilterYear] = useState(String(now.getFullYear()));
  const [filterAccountId, setFilterAccountId] = useState<string>('all');
  const [filterCategoryId, setFilterCategoryId] = useState<string>('all');
  const [filterType, setFilterType] = useState<'all' | 'income' | 'expense'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'paid' | 'unpaid'>('all');
  const [viewMode, setViewMode] = useState<'table' | 'charts'>('table');

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  const years = Array.from({ length: 5 }, (_, i) => String(now.getFullYear() - 2 + i));

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [accRes, catRes, txRes] = await Promise.all([
          api.get('/api/v1/accounts'),
          api.get('/api/v1/categories'),
          api.get(`/api/v1/transactions?competence=${filterMonth === 'all' ? '00' : filterMonth}/${filterYear}`),
        ]);
        setAccounts(accRes.data || []);
        setCategories(catRes.data || []);
        setTransactions(txRes.data || []);
      } catch (err) {
        console.error('Erro ao carregar dados do relatório:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [filterMonth, filterYear]);

  const filtered = useMemo(() => {
    return transactions.filter(t => {
      if (filterAccountId !== 'all' && t.account_id !== Number(filterAccountId)) return false;
      if (filterCategoryId !== 'all' && t.category_id !== Number(filterCategoryId)) return false;
      if (filterType !== 'all' && t.type !== filterType) return false;
      if (filterStatus === 'paid' && !t.is_paid) return false;
      if (filterStatus === 'unpaid' && t.is_paid) return false;
      return true;
    });
  }, [transactions, filterAccountId, filterCategoryId, filterType, filterStatus]);

  const summary = useMemo(() => {
    const income = filtered.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
    const expense = filtered.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
    return { income, expense, balance: income - expense, count: filtered.length };
  }, [filtered]);

  // Gráfico: despesas por categoria (doughnut)
  const expByCatData = useMemo(() => {
    const map: Record<string, number> = {};
    filtered.filter(t => t.type === 'expense').forEach(t => {
      const name = t.category?.name || 'Sem categoria';
      map[name] = (map[name] || 0) + t.amount;
    });
    const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]);
    return {
      labels: sorted.map(([n]) => n),
      datasets: [{ data: sorted.map(([, v]) => v), backgroundColor: CHART_COLORS, borderWidth: 0 }],
    };
  }, [filtered]);

  // Gráfico: receitas por categoria (bar)
  const incByCatData = useMemo(() => {
    const map: Record<string, number> = {};
    filtered.filter(t => t.type === 'income').forEach(t => {
      const name = t.category?.name || 'Sem categoria';
      map[name] = (map[name] || 0) + t.amount;
    });
    const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]);
    return {
      labels: sorted.map(([n]) => n),
      datasets: [{ label: 'Receitas', data: sorted.map(([, v]) => v), backgroundColor: '#10b981', borderRadius: 4 }],
    };
  }, [filtered]);

  // Gráfico: receitas vs despesas por conta (bar agrupado)
  const byAccountData = useMemo(() => {
    const inc: Record<string, number> = {};
    const exp: Record<string, number> = {};
    filtered.forEach(t => {
      const name = t.account?.name || 'Sem conta';
      if (t.type === 'income') inc[name] = (inc[name] || 0) + t.amount;
      if (t.type === 'expense') exp[name] = (exp[name] || 0) + t.amount;
    });
    const labels = Array.from(new Set([...Object.keys(inc), ...Object.keys(exp)]));
    return {
      labels,
      datasets: [
        { label: 'Receitas', data: labels.map(l => inc[l] || 0), backgroundColor: '#10b981', borderRadius: 4 },
        { label: 'Despesas', data: labels.map(l => exp[l] || 0), backgroundColor: '#ef4444', borderRadius: 4 },
      ],
    };
  }, [filtered]);

  // Gráfico: saldo atual das contas (bar)
  const accountBalanceData = useMemo(() => {
    const visible = accounts.filter(a => !a.is_investment);
    return {
      labels: visible.map(a => a.name),
      datasets: [{
        label: 'Saldo Atual',
        data: visible.map(a => a.balance),
        backgroundColor: visible.map(a => a.balance >= 0 ? '#667eea' : '#ef4444'),
        borderRadius: 4,
      }],
    };
  }, [accounts]);

  const barOptions = (title: string) => ({
    responsive: true,
    plugins: {
      legend: { position: 'top' as const },
      title: { display: false },
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => ` ${formatCurrency(ctx.parsed.y ?? ctx.parsed)}`,
        },
      },
    },
    scales: {
      y: { ticks: { callback: (v: any) => formatCurrency(v) } },
    },
  });

  const doughnutOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'right' as const },
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => ` ${formatCurrency(ctx.parsed)}`,
        },
      },
    },
  };

  const display = (val: number) => (isVisible ? formatCurrency(val) : maskCurrency(val));

  const monthLabel = MONTHS.find(m => m.value === filterMonth)?.label ?? filterMonth;

  return (
    <div className="reports-page">

      {/* Filtros */}
      <div className="reports-filters">
        <div className="filter-group">
          <label>Mês</label>
          <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)}>
            {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Ano</label>
          <select value={filterYear} onChange={e => setFilterYear(e.target.value)}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Conta</label>
          <select value={filterAccountId} onChange={e => setFilterAccountId(e.target.value)}>
            <option value="all">Todas</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Categoria</label>
          <select value={filterCategoryId} onChange={e => setFilterCategoryId(e.target.value)}>
            <option value="all">Todas</option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>
                {c.icon ? `${c.icon} ` : ''}{c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Tipo</label>
          <select value={filterType} onChange={e => setFilterType(e.target.value as any)}>
            <option value="all">Todos</option>
            <option value="income">Receitas</option>
            <option value="expense">Despesas</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Status</label>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value as any)}>
            <option value="all">Todos</option>
            <option value="paid">Pago</option>
            <option value="unpaid">Pendente</option>
          </select>
        </div>
      </div>

      {/* Resumo */}
      <div className="reports-summary">
        <div className="summary-card income">
          <span className="summary-label">Receitas</span>
          <span className="summary-value">{display(summary.income)}</span>
        </div>
        <div className="summary-card expense">
          <span className="summary-label">Despesas</span>
          <span className="summary-value">{display(summary.expense)}</span>
        </div>
        <div className={`summary-card balance ${summary.balance >= 0 ? 'positive' : 'negative'}`}>
          <span className="summary-label">Saldo do Período</span>
          <span className="summary-value">{display(summary.balance)}</span>
        </div>
        <div className="summary-card neutral">
          <span className="summary-label">Transações</span>
          <span className="summary-value">{summary.count}</span>
        </div>
      </div>

      {/* Toggle visualização */}
      <div className="reports-header-row">
        <span className="reports-period-label">
          {monthLabel} / {filterYear}
          {filterAccountId !== 'all' && ` · ${accounts.find(a => String(a.id) === filterAccountId)?.name}`}
          {filterCategoryId !== 'all' && ` · ${categories.find(c => String(c.id) === filterCategoryId)?.name}`}
        </span>
        <div className="reports-view-toggle">
          <button className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`} onClick={() => setViewMode('table')}>
            Tabela
          </button>
          <button className={`toggle-btn ${viewMode === 'charts' ? 'active' : ''}`} onClick={() => setViewMode('charts')}>
            Gráficos
          </button>
        </div>
      </div>

      {loading ? (
        <div className="reports-loading">Carregando...</div>
      ) : viewMode === 'table' ? (

        /* Tabela */
        <div className="reports-table-wrapper">
          {filtered.length === 0 ? (
            <div className="reports-empty">Nenhuma transação encontrada para os filtros selecionados.</div>
          ) : (
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Descrição</th>
                  <th>Tipo</th>
                  <th>Vencimento</th>
                  <th>Conta</th>
                  <th>Categoria</th>
                  <th>Status</th>
                  <th className="align-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => (
                  <tr key={t.id}>
                    <td className="td-description">{t.description}</td>
                    <td>
                      <span className={`type-badge ${t.type}`}>
                        {t.type === 'income' ? 'Receita' : t.type === 'expense' ? 'Despesa' : 'Transfer.'}
                      </span>
                    </td>
                    <td>{t.due_date ? formatDate(normalizeDate(t.due_date)) : '—'}</td>
                    <td>{t.account?.name || '—'}</td>
                    <td>{t.category?.name || '—'}</td>
                    <td>
                      <span className={`status-badge ${t.is_paid ? 'paid' : 'unpaid'}`}>
                        {t.is_paid ? 'Pago' : 'Pendente'}
                      </span>
                    </td>
                    <td className={`align-right amount-cell ${t.type}`}>
                      {display(t.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      ) : (

        /* Gráficos */
        <div className="reports-charts">
          {/* Saldo por Conta */}
          {accounts.filter(a => !a.is_investment).length > 0 && (
            <div className="chart-card">
              <h3>Saldo das Contas</h3>
              <div className="chart-wrapper">
                <Bar data={accountBalanceData} options={barOptions('Saldo das Contas')} />
              </div>
            </div>
          )}

          {/* Despesas por Categoria */}
          {filtered.filter(t => t.type === 'expense').length > 0 ? (
            <div className="chart-card">
              <h3>Despesas por Categoria</h3>
              <div className="chart-wrapper doughnut">
                <Doughnut data={expByCatData} options={doughnutOptions} />
              </div>
            </div>
          ) : (
            <div className="chart-card chart-empty">
              <h3>Despesas por Categoria</h3>
              <p>Sem despesas no período selecionado.</p>
            </div>
          )}

          {/* Receitas por Categoria */}
          {filtered.filter(t => t.type === 'income').length > 0 ? (
            <div className="chart-card">
              <h3>Receitas por Categoria</h3>
              <div className="chart-wrapper">
                <Bar data={incByCatData} options={barOptions('Receitas por Categoria')} />
              </div>
            </div>
          ) : (
            <div className="chart-card chart-empty">
              <h3>Receitas por Categoria</h3>
              <p>Sem receitas no período selecionado.</p>
            </div>
          )}

          {/* Receitas vs Despesas por Conta */}
          {filtered.length > 0 && (
            <div className="chart-card full-width">
              <h3>Receitas vs Despesas por Conta</h3>
              <div className="chart-wrapper">
                <Bar data={byAccountData} options={barOptions('Por Conta')} />
              </div>
            </div>
          )}

          {filtered.length === 0 && accounts.filter(a => !a.is_investment).length === 0 && (
            <div className="reports-empty">Nenhum dado encontrado para os filtros selecionados.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default Reports;
